import { useEffect, useState } from "react";
import {
  LABEL_SPECS,
  listRasters,
  printStickers,
  rasterUrl,
  type LabelKind,
} from "./raster";
import { LOCAL_TIXPIX } from "./localPrinter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export function StickerView() {
  const [labelKind, setLabelKind] = useState<LabelKind>("29x90");
  const spec = LABEL_SPECS[labelKind];

  const [available, setAvailable] = useState<string[]>([]);
  const [row, setRow] = useState<string[]>([]);
  const [count, setCount] = useState(1);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [canPrint, setCanPrint] = useState(true);

  useEffect(() => {
    fetch("/api/config")
      .then((response) => response.json())
      .then((config: { canPrint?: boolean }) => setCanPrint(config.canPrint !== false))
      .catch(() => setCanPrint(true));
  }, []);

  useEffect(() => {
    setRow([]);
    setAvailable([]);
    listRasters(spec.rasterSize).then(setAvailable);
  }, [labelKind, spec.rasterSize]);

  const pick = (hex: string) => {
    if (spec.multi) setRow((r) => [...r, hex]);
    else setRow([hex]);
  };
  const removeAt = (i: number) => setRow((r) => r.filter((_, j) => j !== i));
  const clear = () => setRow([]);

  const onPrint = async () => {
    if (row.length === 0) return;
    if (!canPrint) {
      const handoff = new URL("/local-print/stickers", LOCAL_TIXPIX);
      handoff.searchParams.set("label", labelKind);
      for (const hex of row) handoff.searchParams.append("hex", hex);
      handoff.searchParams.set("count", String(count));
      window.open(handoff, "_blank", "noopener");
      setStatus("opened the local print confirmation…");
      return;
    }
    setBusy(true);
    setStatus(`printing ${count}×…`);
    try {
      await printStickers(labelKind, row, count);
      setStatus(`printed ${count}×`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="border-b px-6 py-3 flex items-center gap-3">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={labelKind}
          onValueChange={(v) => v && setLabelKind(v as LabelKind)}
        >
          <ToggleGroupItem value="29x90">DK-1201 · 29×90</ToggleGroupItem>
          <ToggleGroupItem value="62">DK-2205 · 62 cont.</ToggleGroupItem>
        </ToggleGroup>

        <span className="text-xs text-muted-foreground flex-1">{status}</span>

        <Input
          type="number"
          min={1}
          max={20}
          className="w-20"
          value={count}
          onChange={(e) => setCount(Math.max(1, +e.target.value || 1))}
          aria-label="Copies"
        />
        <Button variant="ghost" onClick={clear} disabled={row.length === 0}>
          Clear
        </Button>
        <Button onClick={onPrint} disabled={busy || row.length === 0}>
          {canPrint ? "Print" : "Open local print"}
        </Button>
      </div>

      <div className="flex-1 flex min-h-0">
        <aside className="w-[360px] border-r bg-background flex flex-col min-h-0">
          <div className="text-xs text-muted-foreground px-3 py-2 border-b">
            {available.length === 0
              ? `no ${spec.rasterSize}px rasters available yet`
              : `${available.length} emoji · click to ${spec.multi ? "append" : "select"}`}
          </div>
          <div className="flex-1 overflow-y-auto">
            <div
              className="grid gap-1 p-2"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))" }}
            >
              {available.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  onClick={() => pick(hex)}
                  className="flex items-center justify-center rounded bg-muted/40 hover:bg-muted p-1 aspect-square"
                >
                  <img
                    src={rasterUrl(hex, spec.rasterSize)}
                    alt={hex}
                    className="h-full w-full object-contain"
                    loading="lazy"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="flex-1 flex items-center justify-center overflow-auto bg-muted p-8 min-h-0">
          <div
            className="bg-white shadow-lg flex items-center justify-center"
            style={{ width: spec.canvasW, height: spec.canvasH }}
          >
            {row.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {spec.multi ? "pick emojis from the left" : "pick one emoji from the left"}
              </div>
            ) : (
              row.map((hex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => removeAt(i)}
                  title="click to remove"
                  className="shrink-0 hover:opacity-70 transition-opacity"
                >
                  <img
                    src={rasterUrl(hex, spec.rasterSize)}
                    alt={hex}
                    style={{ height: spec.rasterSize, width: spec.rasterSize }}
                    draggable={false}
                  />
                </button>
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
